import { useQuery, UseQueryOptions } from 'react-query';
import {
  CallRouteService,
  ListPhoneHoursRequest,
  ListPhoneHoursResponse,
  ReadRequest,
  ReadResponse,
  ListRequest,
  ListResponse,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { authorizedFetch } from 'helpers/utils';

const callRouteQueryKeys = {
  base: ['call-route'],
  officeHours: (tenantId: string) => [
    ...callRouteQueryKeys.base,
    'office-hours',
    tenantId,
  ],
  getCallRouteDetail: (callRouteId: string) => [
    ...callRouteQueryKeys.base,
    'call-route',
    callRouteId,
  ],
  getMultiCallRouteDetail: (callRouteIds: string[]) => [
    ...callRouteQueryKeys.base,
    'multi',
    'call-route',
    ...callRouteIds,
  ],
  getCallRoutes: (tenantId: string) => [
    ...callRouteQueryKeys.base,
    'call-routes',
    tenantId,
  ],
};

const CACHE_TIME = 1000 * 60 * 5; // 5 minutes

const useGetPhoneHours = <T = ListPhoneHoursResponse>(
  request: ListPhoneHoursRequest,
  options?: UseQueryOptions<ListPhoneHoursResponse, Error, T>
) => {
  return useQuery({
    queryKey: callRouteQueryKeys.officeHours(request.tenantId),
    queryFn: () => {
      if (!request.tenantId) {
        return Promise.resolve({});
      }
      return CallRouteService.ListPhoneHours(authorizedFetch(), request);
    },
    retry: false,
    ...options,
    enabled: !!request.tenantId && (options?.enabled ?? true),
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });
};

const useGetCallRouteDetail = <T = ReadResponse>(
  request: ReadRequest,
  options?: UseQueryOptions<ReadResponse, Error, T>
) => {
  return useQuery({
    queryKey: callRouteQueryKeys.getCallRouteDetail(request.callRouteId),
    queryFn: () => {
      return CallRouteService.Read(authorizedFetch(), request);
    },
    retry: false,
    ...options,
    enabled: !!request.callRouteId && (options?.enabled ?? true),
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });
};

const useGetMultiCallRouteDetail = <T = ReadResponse[]>(
  requestList: ReadRequest[],
  options?: UseQueryOptions<ReadResponse[], Error, T>
) => {
  const callRouteIdsList = requestList.map(({ callRouteId }) => callRouteId);
  return useQuery({
    queryKey: callRouteQueryKeys.getMultiCallRouteDetail(callRouteIdsList),
    queryFn: () => {
      return Promise.all(
        callRouteIdsList.map((callRouteId) =>
          CallRouteService.Read(authorizedFetch(), { callRouteId })
        )
      );
    },
    retry: false,
    ...options,
    enabled: !!callRouteIdsList.length && (options?.enabled ?? true),
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });
};

const useGetCallRouteList = <T = ListResponse>(
  request: ListRequest,
  options?: UseQueryOptions<ListResponse, Error, T>
) => {
  return useQuery({
    queryKey: callRouteQueryKeys.getCallRoutes(request.tenantId ?? ''),
    queryFn: () => {
      if (!request.tenantId) {
        return Promise.resolve({});
      }
      return CallRouteService.List(authorizedFetch(), request);
    },
    retry: false,
    ...options,
    enabled: !!request.tenantId && (options?.enabled ?? true),
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });
};

export const CallRouteQueries = {
  useGetPhoneHours,
  useGetCallRouteDetail,
  useGetCallRouteList,
  useGetMultiCallRouteDetail,
};
